/* Sitewide product card image, colour and quick size selection. */
(() => {
  const cards = new WeakMap();
  const touches = new WeakMap();
  const suppressClicks = new WeakMap();
  const imageCache = new Map();
  const normalize = (value) => String(value || '').trim().toLocaleLowerCase().replace(/[_-]+/g, ' ');
  const mobileCardViewport = window.matchMedia('(max-width: 699px)');
  // The mobile toggle applies only to its section; collection cards keep image paging.
  const galleryEnabled = (card) => !mobileCardViewport.matches || card?.closest('[data-card-image-carousel]')?.dataset.cardImageCarousel !== 'false';
  const nearbyCards = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const card = entry.target;
      nearbyCards.unobserve(card);
      const data = cards.get(card);
      if (data) {
        warmImages(data);
        showImage(card, data);
      }
    });
  }, { rootMargin: '250px' }) : null;

  function preload(url, urgent = false) {
    if (!url) return null;
    if (imageCache.has(url)) {
      const cached = imageCache.get(url);
      if (urgent) cached.image.fetchPriority = 'high';
      return cached;
    }
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = urgent ? 'high' : 'low';
    image.src = url;
    const ready = typeof image.decode === 'function'
      ? image.decode().catch(() => {})
      : new Promise((resolve) => { image.onload = resolve; image.onerror = resolve; });
    const cached = { image, ready };
    imageCache.set(url, cached);
    return cached;
  }

  function warmImages(data) {
    const gallery = data.imagesForColor || activeImages(data);
    for (let offset = 0; offset < Math.min(4, gallery.length); offset++) preload(gallery[(data.index + offset) % gallery.length]?.src);
    // The first and last frames are both one tap away at the carousel boundary.
    preload(gallery[0]?.src);
    preload(gallery[gallery.length - 1]?.src);
    if (data.colorIndex < 0) return;
    const otherColors = new Set(data.variants.map((variant) => variant.options[data.colorIndex]));
    otherColors.delete(data.color);
    for (const color of [...otherColors].slice(0, 6)) {
      const first = activeImages({ ...data, color })[0];
      if (first) preload(first.src);
    }
  }

  function setup(card) {
    if (cards.has(card)) return cards.get(card);
    const node = card.querySelector('.product-card__data');
    if (!node) return null;
    let data;
    try { data = JSON.parse(node.textContent); } catch (error) { return null; }
    data.color = data.selectedColor;
    data.index = 0;
    cards.set(card, data);
    update(card, data, true, true);
    nearbyCards?.observe(card);
    return data;
  }

  function sameColor(variant, data) {
    return data.colorIndex < 0 || normalize(variant.options[data.colorIndex]) === normalize(data.color);
  }

  function uniqueImages(images) {
    const seen = new Set();
    return images.filter((image) => {
      if (!image.src || seen.has(image.src)) return false;
      seen.add(image.src);
      return true;
    });
  }

  function activeImages(data) {
    if (data.colorIndex < 0) return uniqueImages(data.images);
    const variants = data.variants.filter((variant) => sameColor(variant, data));
    const publishedColors = new Set(data.variants.map((variant) => normalize(variant.options[data.colorIndex])));
    const plainAlts = data.images.map((image) => normalize(image.alt));
    const distinctAlts = [...new Set(plainAlts.filter(Boolean))];
    const consistentlyColorTagged = distinctAlts.length >= 2 && distinctAlts.some((alt) => publishedColors.has(alt))
      && distinctAlts.every((alt) => alt.length <= 32 && plainAlts.filter((value) => value === alt).length >= 2);
    const starts = new Map();
    for (const variant of data.variants) {
      if (!variant.mediaPosition) continue;
      const color = normalize(variant.options[data.colorIndex]);
      starts.set(color, Math.min(starts.get(color) || Infinity, variant.mediaPosition));
    }
    const mediaColor = (image) => {
      const match = String(image.alt || '').match(/#([^#]+)_([^#]+)$/);
      if (match) return normalize(match[2]);
      const plain = normalize(image.alt);
      return plain && (publishedColors.has(plain) || consistentlyColorTagged) ? plain : null;
    };
    for (const image of data.images) {
      const color = mediaColor(image);
      if (color) starts.set(color, Math.min(starts.get(color) || Infinity, image.position));
    }
    const boundaries = [...starts.entries()].sort((a, b) => a[1] - b[1]);
    const selected = normalize(data.color);
    const hasGroups = boundaries.length > 0 || data.images.some((image) => mediaColor(image));
    const images = data.images.filter((image) => {
      const explicit = mediaColor(image);
      if (explicit) return explicit === selected;
      if (!hasGroups) return false;
      let inferred = null;
      for (const [color, position] of boundaries) {
        if (position > image.position) break;
        inferred = color;
      }
      return inferred === selected;
    });
    if (images.length) return uniqueImages(images);
    const variantImage = variants.find((variant) => variant.image)?.image;
    const fallback = data.images.find((image) => image.src === variantImage);
    return fallback ? [fallback] : variantImage ? [{ src: variantImage, alt: '' }] : data.images.slice(0, 1);
  }

  function updateDots(card, data) {
    const dots = card.querySelector('.product-card__dots');
    if (!dots) return;
    const count = data.imagesForColor.length;
    dots.hidden = !galleryEnabled(card) || count < 2;
    if (dots.hidden) {
      dots.replaceChildren();
      return;
    }
    if (dots.children.length !== count) {
      dots.replaceChildren(...Array.from({ length: count }, (_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-card__dot';
        button.dataset.cardDot = String(index);
        button.setAttribute('aria-label', `Show image ${index + 1} of ${count}`);
        return button;
      }));
    }
    for (const [index, button] of Array.from(dots.children).entries()) {
      const active = index === data.index;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
  }

  mobileCardViewport.addEventListener('change', () => {
    document.querySelectorAll('.product-card__data').forEach((node) => {
      const card = node.closest('.product-card');
      const data = card && cards.get(card);
      if (data) updateDots(card, data);
    });
  });

  function update(card, data, reset = false, initial = false) {
    // Limit only product-card previews; the product page gallery remains complete.
    const images = activeImages(data).slice(0, 3);
    data.imagesForColor = images;
    const preferred = data.variants.find((variant) => sameColor(variant, data) && variant.available && variant.image)
      || data.variants.find((variant) => sameColor(variant, data) && variant.image);
    if (reset || data.index >= images.length) {
      const preferredIndex = images.findIndex((image) => image.src === preferred?.image);
      data.index = Math.max(0, preferredIndex);
    }
    // Keep Shopify's responsive, lazy-loaded first image; fetch alternates only for nearby cards.
    if (!initial) showImage(card, data);
    if (!initial) {
      for (let offset = 1; offset <= Math.min(3, images.length - 1); offset++) preload(images[(data.index + offset) % images.length]?.src);
      preload(images[(data.index - 1 + images.length) % images.length]?.src);
      preload(images[0]?.src);
      preload(images[images.length - 1]?.src);
    }
    updateDots(card, data);
    const match = data.variants.find((variant) => variant.available && sameColor(variant, data));
    const bag = card.querySelector('[data-card-bag]');
    if (bag) bag.disabled = !match;
    const input = card.querySelector('.product-card__buy-form [name="id"]');
    if (input && match) input.value = match.id;
    card.querySelectorAll('[data-card-size]').forEach((button) => {
      button.disabled = !data.variants.some((variant) => variant.available && sameColor(variant, data) && normalize(variant.options[data.sizeIndex]) === normalize(button.dataset.cardSize));
    });
  }

  function showImage(card, data) {
    const source = data.imagesForColor[data.index];
    const image = card.querySelector('.product-card__image--primary');
    const token = data.swapToken = (data.swapToken || 0) + 1;
    if (!image || !source) return;
    const shown = new URL(image.currentSrc || image.src, window.location.href);
    const next = new URL(source.src, window.location.href);
    if (shown.pathname === next.pathname) return;
    const cached = preload(source.src, true);
    const swap = () => {
      if (token !== data.swapToken || !cached.image.naturalWidth) return;
      const media = image.closest('.product-card__media');
      const previous = media?.querySelector('.product-card__image--outgoing');
      const visibleImage = previous || image;
      const outgoing = media && visibleImage.cloneNode(false);
      if (outgoing) {
        outgoing.classList.remove('product-card__image--primary', 'is-leaving');
        outgoing.classList.add('product-card__image--outgoing');
        outgoing.removeAttribute('srcset');
        outgoing.removeAttribute('sizes');
        outgoing.src = visibleImage.currentSrc || visibleImage.src;
        outgoing.alt = '';
        outgoing.setAttribute('aria-hidden', 'true');
        outgoing.draggable = false;
        previous?.remove();
        media.append(outgoing);
      }
      image.srcset = '';
      image.src = source.src;
      image.alt = source.alt || card.querySelector('.product-title')?.textContent.trim() || '';
      if (outgoing) {
        void outgoing.offsetWidth;
        outgoing.classList.add('is-leaving');
        const cleanup = () => outgoing.remove();
        outgoing.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 350);
      }
    };
    if (cached.image.complete && cached.image.naturalWidth) swap();
    else cached.ready.then(swap);
  }

  function closePanel(card) {
    const panel = card.querySelector('.product-card__size-panel');
    if (!panel) return;
    panel.classList.remove('is-open');
    card.querySelector('[data-card-bag]')?.setAttribute('aria-expanded', 'false');
  }

  function openPanel(card) {
    const panel = card.querySelector('.product-card__size-panel');
    if (!panel) return;
    const fitNote = card.querySelector('[data-card-size-fit-note]');
    if (fitNote) fitNote.hidden = true;
    // Let the initial hidden state paint before moving the strip into view.
    panel.hidden = false;
    void panel.offsetHeight;
    panel.classList.add('is-open');
    card.querySelector('[data-card-bag]')?.setAttribute('aria-expanded', 'true');
  }

  function advance(card, data, direction) {
    if (!galleryEnabled(card) || data.imagesForColor.length < 2) return;
    data.index = (data.index + direction + data.imagesForColor.length) % data.imagesForColor.length;
    update(card, data);
  }

  function submit(card, variant) {
    if (!variant?.available) return;
    const form = card.querySelector('.product-card__buy-form form');
    if (!form) return;
    form.elements.id.value = variant.id;
    form.requestSubmit(form.querySelector('.product-card__submit'));
    closePanel(card);
  }

  document.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) return;
    const media = event.target.closest?.('.product-card__media');
    if (!media || !galleryEnabled(media.closest('.product-card'))) return;
    suppressClicks.delete(media);
    touches.set(media, { x: event.touches[0].clientX, y: event.touches[0].clientY });
  }, { passive: true });
  document.addEventListener('touchend', (event) => {
    const media = event.target.closest?.('.product-card__media');
    const start = media && touches.get(media);
    if (!start || event.changedTouches.length !== 1) return;
    touches.delete(media);
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < 35 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
    const card = media.closest('.product-card');
    const data = card && setup(card);
    if (!data || !galleryEnabled(card) || data.imagesForColor.length < 2) return;
    suppressClicks.set(media, Date.now() + 650);
    advance(card, data, dx < 0 ? 1 : -1);
  }, { passive: true });
  document.addEventListener('touchcancel', (event) => {
    const media = event.target.closest?.('.product-card__media');
    if (media) touches.delete(media);
  }, { passive: true });
  document.addEventListener('click', (event) => {
    const media = event.target.closest?.('.product-card__media');
    if (media && (suppressClicks.get(media) || 0) > Date.now()) {
      suppressClicks.delete(media);
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-card-color], [data-card-dot], [data-card-bag], [data-card-size], [data-card-close]');
    if (!target) return;
    const card = target.closest('.product-card');
    if (!card) return;
    const data = setup(card);
    if (!data) return;
    if (target.matches('[data-card-color]')) {
      if (target.dataset.productUrl) {
        window.location.href = target.dataset.productUrl;
        return;
      }
      data.color = target.dataset.cardColor;
      data.index = 0;
      const options = card.querySelectorAll('[data-card-color]');
      options.forEach((button) => {
        const selected = button === target;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const match = data.variants.find((variant) => variant.available && sameColor(variant, data)) || data.variants.find((variant) => sameColor(variant, data));
      card.querySelectorAll('a.product-card__media, a.product-title').forEach((link) => {
        const url = new URL(link.href, window.location.href);
        if (match) url.searchParams.set('variant', match.id);
        link.href = url.href;
      });
      const modal = card.querySelector('quick-buy-modal[handle]');
      if (modal && match) modal.setAttribute('handle', `${card.getAttribute('handle')}?variant=${match.id}`);
      closePanel(card);
      const fitNote = card.querySelector('[data-card-size-fit-note]');
      if (fitNote) fitNote.hidden = true;
      update(card, data, true);
    } else if (target.matches('[data-card-dot]')) {
      const index = Number(target.dataset.cardDot);
      if (galleryEnabled(card) && Number.isInteger(index) && index >= 0 && index < data.imagesForColor.length) {
        data.index = index;
        update(card, data);
      }
    } else if (target.matches('[data-card-close]')) {
      closePanel(card);
    } else if (target.matches('[data-card-bag]')) {
      const panel = card.querySelector('.product-card__size-panel');
      if (panel) {
        if (panel.classList.contains('is-open')) closePanel(card);
        else openPanel(card);
      } else {
        submit(card, data.variants.find((variant) => variant.available && sameColor(variant, data)));
      }
    } else if (target.matches('[data-card-size]')) {
      const fitNote = card.querySelector('[data-card-size-fit-note]');
      if (fitNote) fitNote.hidden = false;
      submit(card, data.variants.find((variant) => variant.available && sameColor(variant, data) && normalize(variant.options[data.sizeIndex]) === normalize(target.dataset.cardSize)));
    }
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.product-card__size-panel.is-open').forEach((panel) => {
      if (!panel.closest('.product-card').contains(event.target)) closePanel(panel.closest('.product-card'));
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') document.querySelectorAll('.product-card__size-panel.is-open').forEach((panel) => closePanel(panel.closest('.product-card')));
  });
  const initialize = (root) => {
    if (root.matches?.('.product-card')) setup(root);
    root.querySelectorAll?.('.product-card').forEach(setup);
  };
  // Recommendations, recently viewed products, and filtered/search results insert
  // cards after DOMContentLoaded without dispatching shopify:section:load.
  const watchCards = () => {
    initialize(document);
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) initialize(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCards, { once: true });
  else watchCards();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
